import { useTranslation } from 'react-i18next';
import type { AnalyticsFactDto } from '../../api/analytics';
import { formatMoney } from '../../utils/formatMoney';

interface SuggestionStripProps {
  facts: AnalyticsFactDto[] | undefined;
  onSelect: (targetView: string) => void;
}

function formatFactValue(fact: AnalyticsFactDto): string {
  if (fact.format === 'money') return formatMoney(fact.value);
  if (fact.format === 'percent') return `${formatMoney(fact.value, false)} %`;
  return String(fact.value);
}

/**
 * Suggestion strip: deterministic, server-computed facts (never model
 * arithmetic) phrased as investigation chips. Clicking a chip drills into
 * the fact's target view.
 */
export default function SuggestionStrip({ facts, onSelect }: SuggestionStripProps) {
  const { t } = useTranslation();
  if (!facts || facts.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {facts.map(fact => (
        <button
          key={fact.key}
          onClick={fact.targetView ? () => onSelect(fact.targetView!) : undefined}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors ${
            fact.targetView
              ? 'bg-primary-50 border-primary-200 text-primary-800 hover:border-primary-400'
              : 'bg-gray-50 border-gray-200 text-gray-600 cursor-default'
          }`}
        >
          {t(`analytics.fact.${fact.key}`, { value: formatFactValue(fact) })}
        </button>
      ))}
    </div>
  );
}
